import React from 'react';
import { useNavigate } from 'react-router-dom';

interface Calculator {
  id: string;
  name: string;
  description: string;
  icon: string;
  link: string;
  type: 'internal' | 'external';
}

const CalculatorSection: React.FC = () => {
  const navigate = useNavigate();

  const calculators: Calculator[] = [
    {
      id: 'compounding',
      name: 'Compounding Power',
      description: 'Discover how small increases in returns can dramatically impact your wealth over time.',
      icon: '💰',
      link: '/calculators/compounding',
      type: 'external'
    },
    {
      id: 'stepup-sip',
      name: 'Step-up SIP Calculator',
      description: 'Discover the power of stepping up your SIP investments and see how it can boost your wealth.',
      icon: '🏦',
      link: '/calculators/stepup',
      type: 'external'
    },
    {
      id: 'early-investment',
      name: 'Importance of Starting Early', // changed this name from early-investment to Importance of Starting Early
      description: 'Understand the importance of starting early - see how a few years can make a massive difference.',
      icon: '⏰',
      link: '/calculators/early',
      type: 'external'
    },
    {
      id: 'retirement-planner',
      name: 'Retirement Planner',
      description: 'Calculate how much you need to save for a comfortable retirement based on your lifestyle and future expectations.',
      icon: '⏳',
      link: '/calculators/retirement',
      type: 'external'
    },
  ];

  const handleCalculatorClick = (calculator: Calculator) => {
    if (calculator.type === 'external') {
      navigate(calculator.link); // React internal navigation
    } else {
      alert(`${calculator.name} is coming soon! Stay tuned for more financial planning tools.`);
    }
  };

  return (
    <div className="py-8">
      {/* Header Section */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl">
          Financial
          <span className="text-primary-600"> Calculators</span>
        </h1>
        <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
          Use our comprehensive suite of financial calculators to plan your investments, 
          loans, and financial goals with precision.
        </p>
      </div>

      {/* Calculators Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {calculators.map((calculator) => (
          <div
            key={calculator.id}
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer border border-gray-200"
            onClick={() => handleCalculatorClick(calculator)}
          >
            <div className="flex items-center mb-4">
              <span className="text-4xl mr-4">{calculator.icon}</span>
              <h3 className="text-xl font-semibold text-gray-900">{calculator.name}</h3>
            </div>
            
            <p className="text-gray-600 mb-4 leading-relaxed">
              {calculator.description}
            </p>
            
            <div className="flex items-center justify-between">
              <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                calculator.type === 'external' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {calculator.type === 'external' ? 'Available' : 'Coming Soon'}
              </span>
              
              <button className="text-primary-600 hover:text-primary-700 font-medium flex items-center">
                {calculator.type === 'external' ? 'Use Calculator' : 'Learn More'}
                <svg 
                  className="ml-1 w-4 h-4" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 5l7 7-7 7" 
                  />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Additional Information */}
      <div className="mt-12 bg-primary-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Need Help with Calculations?
        </h3>
        <p className="text-gray-600 mb-4">
          Our financial advisors can help you interpret these calculations and create a 
          personalized financial plan based on your goals.
        </p>
        <button className="bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700 transition-colors">
          <a href='/'>Find an Advisor</a>
        </button>
      </div>
    </div>
  );
};

export default CalculatorSection;
